import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '../components/AppLayout';
import LoadingSkeleton from '../components/LoadingSkeleton';
import SessionItem from '../components/SessionItem';

import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import { useError } from '../contexts/ErrorContext';
import { useWebSocket, useWebSocketMessage } from '../contexts/WebSocketContext';
import { POLLING_CONFIG } from '../config/constants';
import { extractErrorMessage } from '../utils/errorHandling';
import {
  vpnApi,
  VPNStatusResponse,
  VPNSessionListResponse,
  VPNConnectionRequest,
  VPNEnableRequest,
  VPNConfigWithQRResponse
} from '../api/vpn';
import {
  WifiIcon,
  ServerIcon,
  UserGroupIcon,
  PlayIcon,
  StopIcon,
  ArrowLeftIcon,
  ClipboardDocumentIcon,
  ClockIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

const Connections: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { addError } = useError();
  const { isConnected: wsConnected } = useWebSocket();

  const [vpnStatus, setVpnStatus] = useState<VPNStatusResponse | null>(null);
  const [sessions, setSessions] = useState<VPNSessionListResponse | null>(null);
  const [serverConfig, setServerConfig] = useState<VPNConfigWithQRResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // VPN Enable/Disable state
  const [isToggling, setIsToggling] = useState(false);
  const [enableForm, setEnableForm] = useState<VPNEnableRequest>({
    max_concurrent_connections: 5,
    bandwidth_limit_mbps: undefined,
    daily_data_limit_gb: undefined,
  });

  // Connection request state
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionForm, setConnectionForm] = useState<VPNConnectionRequest>({
    friend_code: '',
    host_device_id: undefined,
    session_name: '',
    bandwidth_limit_mbps: undefined,
    data_limit_gb: undefined,
    time_limit_minutes: undefined,
  });

  // Define callbacks first
  const fetchAllData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [statusResponse, sessionsResponse] = await Promise.all([
        vpnApi.getStatus(),
        vpnApi.getSessions(),
      ]);

      setVpnStatus(statusResponse.data);
      setSessions(sessionsResponse.data);

      // If sharing is enabled, get server config
      if (statusResponse.data.sharing_enabled) {
        try {
          const configResponse = await vpnApi.getServerConfig();
          setServerConfig(configResponse.data);
        } catch (error) {
          const apiError = extractErrorMessage(error, 'Failed to fetch server config');
          addError(apiError.message);
        }
      }
    } catch (error) {
      const apiError = extractErrorMessage(error, 'Failed to fetch VPN data');
      addError(apiError.message);
    } finally {
      setIsLoading(false);
    }
  }, [addError]);

  const refreshData = useCallback(async () => {
    setIsRefreshing(true);
    await fetchAllData();
    setIsRefreshing(false);
  }, [fetchAllData]);

  // Initial data fetch on mount
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Pre-fill friend code from navigation state
  useEffect(() => {
    const state = location.state as { friendCode?: string } | null;
    if (state?.friendCode) {
      setConnectionForm(prev => ({ ...prev, friend_code: state.friendCode! }));
      toast.info('Friend code pre-filled. Review and submit to connect.');

      // Clear navigation state to prevent re-triggering
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname, toast]);

  // WebSocket real-time updates for VPN events
  useWebSocketMessage('vpn_status_changed', () => {
    refreshData();
  });

  useWebSocketMessage('session_created', () => {
    refreshData();
  });

  useWebSocketMessage('session_updated', () => {
    refreshData();
  });

  useWebSocketMessage('session_ended', () => {
    refreshData();
  });

  // Fallback: Auto-refresh with polling ONLY if WebSocket not connected
  useEffect(() => {
    // If WebSocket is connected, don't poll
    if (wsConnected) {
      return;
    }
    let intervalId: NodeJS.Timeout;

    const startPolling = () => {
      const interval = document.hidden
        ? POLLING_CONFIG.CONNECTIONS_REFRESH_INTERVAL_SLOW
        : POLLING_CONFIG.CONNECTIONS_REFRESH_INTERVAL;

      intervalId = setInterval(() => {
        refreshData();
      }, interval);
    };

    const handleVisibilityChange = () => {
      clearInterval(intervalId);
      startPolling();
    };

    startPolling();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refreshData, wsConnected]);

  const toggleSharing = async () => {
    if (!vpnStatus) return;

    try {
      setIsToggling(true);
      if (vpnStatus.sharing_enabled) {
        await vpnApi.disableSharing();
        toast.success('VPN sharing disabled');
        setServerConfig(null);
      } else {
        await vpnApi.enableSharing(enableForm);
        toast.success('VPN sharing enabled');
        // Fetch server config after enabling
        try {
          const configResponse = await vpnApi.getServerConfig();
          setServerConfig(configResponse.data);
        } catch (error) {
          console.error('Failed to fetch server config:', error);
        }
      }
      await fetchAllData();
    } catch (error) {
      const apiError = extractErrorMessage(error, `Failed to ${vpnStatus.sharing_enabled ? 'disable' : 'enable'} sharing`);
      addError(apiError.message);
    } finally {
      setIsToggling(false);
    }
  };

  const connectToFriend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!connectionForm.friend_code?.trim()) {
      toast.error('Please enter a friend code');
      return;
    }

    try {
      setIsConnecting(true);
      const response = await vpnApi.requestConnection(connectionForm);
      toast.success('Connection request sent! ⚡ Waiting for approval', response.data.message);
      setConnectionForm({
        friend_code: '',
        session_name: '',
        bandwidth_limit_mbps: undefined,
        data_limit_gb: undefined,
        time_limit_minutes: undefined,
      });
      await fetchAllData();
    } catch (error) {
      const apiError = extractErrorMessage(error, 'Failed to connect');
      addError(apiError.message);
    } finally {
      setIsConnecting(false);
    }
  };

  const handleSessionAction = async (sessionId: string, action: 'approve' | 'reject' | 'disconnect') => {
    try {
      await vpnApi.performSessionAction(sessionId, { action });
      toast.success(`Session ${action}d successfully`);
      await fetchAllData();
    } catch (error) {
      const apiError = extractErrorMessage(error, `Failed to ${action} session`);
      addError(apiError.message);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  if (isLoading) {
    return (
      <AppLayout>
        <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8 space-y-8">
          <LoadingSkeleton type="card" count={3} />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/dashboard')}
                className="inline-flex items-center mr-4 text-gray-500 hover:text-gray-700"
              >
                <ArrowLeftIcon className="h-5 w-5 mr-1" />
                Back to Dashboard
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">VPN Connections</h1>
                <p className="text-sm text-gray-600 mt-1">Manage your VPN sharing and connections</p>
              </div>
            </div>
            <button
              onClick={refreshData}
              disabled={isRefreshing}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            >
              <ArrowPathIcon className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8 space-y-8">
        {/* VPN Status */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <ServerIcon className="h-6 w-6 text-gray-400 mr-3" />
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  VPN Sharing Status
                </h3>
              </div>
              <div className="flex items-center space-x-2">
                {vpnStatus?.sharing_enabled ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <WifiIcon className="h-4 w-4 mr-1" />
                    Active
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    Inactive
                  </span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-sm font-medium text-gray-500">Active Connections</div>
                <div className="text-lg font-semibold text-gray-900">
                  {vpnStatus?.active_connections || 0} / {vpnStatus?.max_concurrent_connections || 0}
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-sm font-medium text-gray-500">Sessions Hosted</div>
                <div className="text-lg font-semibold text-gray-900">
                  {vpnStatus?.total_sessions_hosted || 0}
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-sm font-medium text-gray-500">Sessions Consumed</div>
                <div className="text-lg font-semibold text-gray-900">
                  {vpnStatus?.total_sessions_consumed || 0}
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-sm font-medium text-gray-500">VPN IP</div>
                <div className="text-lg font-semibold text-gray-900">
                  {vpnStatus?.vpn_ip || 'Not assigned'}
                </div>
              </div>
            </div>

            {/* VPN Control */}
            <div className="space-y-4">
              {!vpnStatus?.sharing_enabled && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-primary-800 mb-3">Enable VPN Sharing</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-primary-700">
                        Max Connections
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="20"
                        value={enableForm.max_concurrent_connections || 5}
                        onChange={(e) => setEnableForm({
                          ...enableForm,
                          max_concurrent_connections: parseInt(e.target.value)
                        })}
                        className="mt-1 block w-full border-blue-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-primary-700">
                        Bandwidth Limit (Mbps)
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={enableForm.bandwidth_limit_mbps || ''}
                        onChange={(e) => setEnableForm({
                          ...enableForm,
                          bandwidth_limit_mbps: e.target.value ? parseInt(e.target.value) : undefined
                        })}
                        placeholder="No limit"
                        className="mt-1 block w-full border-blue-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-primary-700">
                        Daily Data Limit (GB)
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={enableForm.daily_data_limit_gb || ''}
                        onChange={(e) => setEnableForm({
                          ...enableForm,
                          daily_data_limit_gb: e.target.value ? parseInt(e.target.value) : undefined
                        })}
                        placeholder="No limit"
                        className="mt-1 block w-full border-blue-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-gray-600">
                    {vpnStatus?.sharing_enabled
                      ? 'Your VPN is active and accepting connections from friends'
                      : 'Enable VPN sharing to allow friends to connect through your connection'
                    }
                  </p>
                </div>
                <button
                  onClick={toggleSharing}
                  disabled={isToggling}
                  className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 ${
                    vpnStatus?.sharing_enabled
                      ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                      : 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                  }`}
                >
                  {isToggling ? (
                    <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                  ) : vpnStatus?.sharing_enabled ? (
                    <StopIcon className="h-4 w-4 mr-2" />
                  ) : (
                    <PlayIcon className="h-4 w-4 mr-2" />
                  )}
                  {isToggling
                    ? (vpnStatus?.sharing_enabled ? 'Disabling...' : 'Enabling...')
                    : (vpnStatus?.sharing_enabled ? 'Disable Sharing' : 'Enable Sharing')
                  }
                </button>
              </div>
            </div>

            {/* Server Config */}
            {serverConfig && (
              <div className="mt-6 border-t pt-6">
                <h4 className="text-md font-medium text-gray-900 mb-4">Server Configuration</h4>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      WireGuard Configuration
                    </label>
                    <div className="flex">
                      <textarea
                        readOnly
                        value={serverConfig.config}
                        className="flex-1 block border-gray-300 rounded-l-md shadow-sm bg-gray-50 sm:text-sm font-mono text-xs"
                        rows={8}
                      />
                      <button
                        onClick={() => copyToClipboard(serverConfig.config, 'WireGuard config')}
                        className="inline-flex items-center px-3 py-2 border border-l-0 border-gray-300 rounded-r-md bg-gray-50 text-gray-500 hover:text-gray-700"
                      >
                        <ClipboardDocumentIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>

                  {serverConfig.qr_code && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        QR Code
                      </label>
                      <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
                        {serverConfig.qr_code_svg && (
                          <img
                            src={`data:image/svg+xml;base64,${btoa(serverConfig.qr_code_svg)}`}
                            alt="WireGuard Configuration QR Code"
                            className="inline-block max-w-full h-auto"
                          />
                        )}
                        <p className="text-xs text-gray-500 mt-2">
                          Scan with WireGuard app to connect
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Connect to Friend */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center mb-4">
                <UserGroupIcon className="h-6 w-6 text-gray-400 mr-3" />
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Connect to Friend
                </h3>
              </div>

              <form onSubmit={connectToFriend} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Friend Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={connectionForm.friend_code || ''}
                    onChange={(e) => setConnectionForm({ ...connectionForm, friend_code: e.target.value })}
                    placeholder="Enter friend's code"
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">Required: Get this code from your friend's profile</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Session Name <span className="text-gray-400 text-xs">(Optional)</span>
                  </label>
                  <input
                    type="text"
                    value={connectionForm.session_name || ''}
                    onChange={(e) => setConnectionForm({ ...connectionForm, session_name: e.target.value })}
                    placeholder="e.g., Gaming Session"
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Specific Device <span className="text-gray-400 text-xs">(Optional)</span>
                  </label>
                  <input
                    type="text"
                    value={connectionForm.host_device_id || ''}
                    onChange={(e) => setConnectionForm({ ...connectionForm, host_device_id: e.target.value || undefined })}
                    placeholder="Leave empty to connect to any available device"
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Optional: Specify a device ID to connect to a specific host device
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Bandwidth Limit <span className="text-gray-400 text-xs">(Optional)</span>
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={connectionForm.bandwidth_limit_mbps || ''}
                      onChange={(e) => setConnectionForm({
                        ...connectionForm,
                        bandwidth_limit_mbps: e.target.value ? parseInt(e.target.value) : undefined
                      })}
                      placeholder="No limit (Mbps)"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Time Limit <span className="text-gray-400 text-xs">(Optional)</span>
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={connectionForm.time_limit_minutes || ''}
                      onChange={(e) => setConnectionForm({
                        ...connectionForm,
                        time_limit_minutes: e.target.value ? parseInt(e.target.value) : undefined
                      })}
                      placeholder="No limit (Minutes)"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isConnecting}
                  className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                >
                  {isConnecting ? (
                    <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <WifiIcon className="h-4 w-4 mr-2" />
                  )}
                  {isConnecting ? 'Connecting...' : 'Request Connection'}
                </button>
              </form>
            </div>
          </div>

          {/* Active Sessions */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                Active Sessions
              </h3>

              {(!sessions?.hosted_sessions?.length && !sessions?.client_sessions?.length) ? (
                <div className="text-center py-8">
                  <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No active sessions</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Start sharing or connect to a friend to see sessions here.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Hosted Sessions */}
                  {sessions?.hosted_sessions?.map((session) => (
                    <SessionItem
                      key={session.session_id}
                      session={session}
                      variant="hosted"
                      onDisconnect={(sessionId) => handleSessionAction(sessionId, 'disconnect')}
                    />
                  ))}

                  {/* Client Sessions */}
                  {sessions?.client_sessions?.map((session) => (
                    <SessionItem
                      key={session.session_id}
                      session={session}
                      variant="client"
                      onDisconnect={(sessionId) => handleSessionAction(sessionId, 'disconnect')}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </AppLayout>
  );
};

export default Connections;
